import { formatDate, type Expansion } from "@elektr/shared";
import { useQuery } from "@tanstack/react-query";

import { Badge, Card } from "@/components/ui/card";
import { api, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";

const STATUS = {
  pending: "server.expand_status_pending",
  volume: "server.expand_status_volume",
  filesystem: "server.expand_status_filesystem",
  done: "server.expand_status_done",
  failed: "server.expand_status_failed",
} as const;
const TONE = {
  pending: "info",
  volume: "info",
  filesystem: "info",
  done: "success",
  failed: "danger",
} as const;

const ACTIVE = ["pending", "volume", "filesystem"];

/** Ko'rsatiladigan oxirgi kengaytirish: faol yoki 24 soat ichida tugagan. */
function pickVisible(list: Expansion[]): Expansion | null {
  const last = list[0];
  if (!last) return null;
  if (ACTIVE.includes(last.status)) return last;
  return last.finished_at && Date.now() - new Date(last.finished_at).getTime() < 24 * 3600 * 1000
    ? last
    : null;
}

/** Faol kengaytirishni 3 soniyada bir yangilab ko'rsatadi; tugaganini 24 soat ko'rsatadi. */
export function ExpansionProgress({ enabled }: { enabled: boolean }) {
  const { t } = useT();
  const q = useQuery({
    queryKey: ["hosting", "expansions"],
    enabled,
    queryFn: async () => unwrap(await api.GET("/api/hosting/expand/")),
    select: pickVisible,
    refetchInterval: (query) =>
      query.state.data?.some((e) => ["pending", "volume", "filesystem"].includes(e.status))
        ? 3000
        : false,
  });
  const last = q.data;
  if (!last) return null;
  return (
    <Card className="mb-4" data-testid="expansion-progress">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-bold">{t("server.expand_progress")}</h2>
        <Badge tone={TONE[last.status]}>{t(STATUS[last.status])}</Badge>
        <span className="text-sm">
          {t("server.expand_from_to", { from: String(last.from_gb), to: String(last.to_gb) })}
        </span>
        <span className="text-xs text-muted-foreground">{formatDate(last.created_at)}</span>
      </div>
      {last.message && (
        <p
          role={last.status === "failed" ? "alert" : undefined}
          className={
            last.status === "failed"
              ? "mt-2 text-sm text-danger"
              : "mt-2 text-sm text-muted-foreground"
          }
        >
          {last.message}
        </p>
      )}
    </Card>
  );
}
